import { useState, useEffect, useMemo, useRef } from 'react';
import { debounce } from 'lodash';

import {
  buildExpressionValue,
  parseExpressionValue,
  filterNumberInputOptions,
  getNextLetter,
  generatePresetExpression,
  validateCustomExpression,
  canFieldBeUsedInExpression,
  reletterDataFields
} from './utils';


/**
 * useArithmeticExpression - all state + logic for arithmetic expression building.
 *
 * Extracted from ArithmeticExpressionInput so it can be consumed directly
 * by MetricCard's ConfigForm (split across DataSection + ConfigureSection).
 * Other widgets still use ArithmeticExpressionInput which wraps this hook.
 *
 * @param {Object} params
 * @param {Array} params.allOptions - All dimension options (all types)
 * @param {Object} params.value - Current dimension value (single field or expression object)
 * @param {Function} params.onChange - Callback when value changes
 * @param {Boolean} params.disabled - Whether input is disabled
 * @returns {Object} All state, handlers, and computed values
 */
export const useArithmeticExpression = ({ allOptions, value, onChange, disabled }) => {
  // State
  const [dataFields, setDataFields] = useState({ A: null });
  const [aggregateType, setAggregateType] = useState(null);
  const [customExpression, setCustomExpression] = useState('');
  const [validationError, setValidationError] = useState(null);

  // Parse initial value into state
  useEffect(() => {
    const parsed = parseExpressionValue(value);
    setDataFields(parsed.dataFields);
    setAggregateType(parsed.aggregateType);
    setCustomExpression(parsed.aggregateExpression || '');
  }, [value]);

  // Get NumberInput-only options for B, C, D fields (same content type as A)
  const numberInputOptions = useMemo(() => {
    const fieldA = dataFields.A;
    if (!fieldA?.contentType) return [];
    return filterNumberInputOptions(allOptions, fieldA.contentType);
  }, [allOptions, dataFields.A?.contentType]);

  // Check if + button should be enabled (field A must be direct NumberInput, no rollup)
  const canAddFields = canFieldBeUsedInExpression(dataFields.A);

  // Debounced emit for custom expression changes
  const debouncedEmitRef = useRef();

  // Create debounced function that validates and emits
  useEffect(() => {
    debouncedEmitRef.current = debounce((fields, expression) => {
      const validation = validateCustomExpression(expression, fields);
      if (validation.valid) {
        setValidationError(null);
        const builtValue = buildExpressionValue(fields, expression, 'custom');
        onChange(builtValue);
      } else {
        setValidationError(validation.error);
      }
    }, 400);

    return () => {
      debouncedEmitRef.current?.cancel();
    };
  }, [onChange]);

  // Rebuild and emit value (for non-custom or immediate changes)
  const rebuildAndEmitValue = (fields, type, expression) => {
    const nonNullFields = Object.keys(fields).filter(k => fields[k] != null);

    if (nonNullFields.length === 0) {
      onChange(null);
      return;
    }

    if (nonNullFields.length === 1) {
      onChange(fields[nonNullFields[0]]);
      return;
    }

    if (type && expression) {
      // Validate expression before emitting
      const validation = validateCustomExpression(expression, fields);
      if (validation.valid) {
        const builtValue = buildExpressionValue(fields, expression, type);
        onChange(builtValue);
      }
    }
  };

  // Handle field A change
  const handleFieldAChange = (newField) => {
    // If changing to non-NumberInput or rollup, clear all other fields
    if (!canFieldBeUsedInExpression(newField)) {
      setDataFields({ A: newField });
      setAggregateType(null);
      setCustomExpression('');
      onChange(newField); // Just pass single field
    } else {
      const newDataFields = { ...dataFields, A: newField };
      setDataFields(newDataFields);

      // If only A, pass single field; otherwise rebuild expression
      if (Object.keys(dataFields).length === 1) {
        onChange(newField);
      } else {
        rebuildAndEmitValue(newDataFields, aggregateType, customExpression);
      }
    }
  };

  // Handle adding new field (B, C, D...)
  // If selectedField is provided, the new slot is pre-filled and value is emitted immediately.
  const handleAddField = (selectedField) => {
    const nextLetter = getNextLetter(dataFields);
    const newDataFields = { ...dataFields, [nextLetter]: selectedField || null };
    setDataFields(newDataFields);

    // If this is the second field being added, set default aggregate type
    if (Object.keys(dataFields).length === 1) {
      setAggregateType('sumAll');
      const expression = generatePresetExpression('sumAll', newDataFields);
      setCustomExpression(expression);

      // If a field was pre-selected, emit the value immediately
      if (selectedField) {
        rebuildAndEmitValue(newDataFields, 'sumAll', expression);
      }
    } else if (selectedField && aggregateType) {
      // Field pre-selected and we already have an aggregate — emit immediately
      const expression = aggregateType === 'custom'
        ? customExpression
        : generatePresetExpression(aggregateType, newDataFields);
      rebuildAndEmitValue(newDataFields, aggregateType, expression);
    }
  };

  // Handle field B, C, D... change
  const handleFieldChange = (letter, newField) => {
    const newDataFields = { ...dataFields, [letter]: newField };
    setDataFields(newDataFields);

    // Update expression with new letters
    const expression = aggregateType === 'custom'
      ? customExpression
      : generatePresetExpression(aggregateType, newDataFields);

    rebuildAndEmitValue(newDataFields, aggregateType, expression);
  };

  // Handle removing field
  const handleRemoveField = (letter) => {
    const newDataFields = { ...dataFields };
    delete newDataFields[letter];

    // Reletter to maintain contiguous A, B, C order
    const relettered = reletterDataFields(newDataFields);
    setDataFields(relettered);

    // If only A left, revert to single field
    if (Object.keys(relettered).length === 1) {
      setAggregateType(null);
      setCustomExpression('');
      onChange(relettered.A);
    } else {
      // Regenerate expression with new letters
      const expression = aggregateType === 'custom'
        ? customExpression // Custom expressions might be invalid after removal
        : generatePresetExpression(aggregateType, relettered);
      setCustomExpression(expression);
      rebuildAndEmitValue(relettered, aggregateType, expression);
    }
  };

  // Handle aggregate type change
  const handleAggregateChange = (_, newTypeOption) => {
    const newType = newTypeOption?.value;
    setAggregateType(newType);

    if (newType === 'custom') {
      // For custom, validate and emit current expression immediately
      const validation = validateCustomExpression(customExpression, dataFields);
      if (validation.valid) {
        setValidationError(null);
        rebuildAndEmitValue(dataFields, newType, customExpression);
      } else {
        setValidationError(validation.error);
      }
    } else {
      // Generate preset expression
      const expr = generatePresetExpression(newType, dataFields);
      setCustomExpression(expr);
      setValidationError(null);
      rebuildAndEmitValue(dataFields, newType, expr);
    }
  };

  // Handle custom expression change with debounced validation & emit
  const handleCustomExpressionChange = (_, newExpression) => {
    setCustomExpression(newExpression);

    // Real-time validation feedback (immediate)
    const validation = validateCustomExpression(newExpression, dataFields);
    if (!validation.valid) {
      setValidationError(validation.error);
    } else {
      setValidationError(null);
    }

    // Debounced emit (only fires if valid, after user stops typing)
    debouncedEmitRef.current?.(dataFields, newExpression);
  };

  // Aggregate dropdown options
  const aggregateOptions = useMemo(() => {
    const letters = Object.keys(dataFields)
      .filter(k => dataFields[k] != null)
      .sort();

    if (letters.length < 2) return [];

    // Helper to format display - abbreviated if more than 3 letters
    const formatDisplay = (lettersArr, operator) => {
      if (lettersArr.length <= 3) {
        return lettersArr.join(` ${operator} `);
      }
      // For 4+ letters: A + B + ... + N
      return `${lettersArr[0]} ${operator} ${lettersArr[1]} ${operator} ... ${operator} ${lettersArr[lettersArr.length - 1]}`;
    };

    // For reverse subtraction: N - ... - B - A
    const reversedLetters = [...letters].reverse();

    return [
      { display: formatDisplay(letters, '+'), value: 'sumAll' },
      { display: formatDisplay(letters, '-'), value: 'subtractAll' },
      { display: formatDisplay(reversedLetters, '-'), value: 'subtractAllReverse' },
      { display: 'Custom', value: 'custom' }
    ];
  }, [dataFields]);

  const hasMultipleFields = Object.keys(dataFields).filter(k => dataFields[k] != null).length > 1
    || Object.keys(dataFields).length > 1;

  const sortedLetters = Object.keys(dataFields).sort();

  return {
    // State
    dataFields,
    aggregateType,
    customExpression,
    validationError,

    // Handlers
    handleFieldAChange,
    handleAddField,
    handleFieldChange,
    handleRemoveField,
    handleAggregateChange,
    handleCustomExpressionChange,

    // Computed
    numberInputOptions,
    canAddFields,
    aggregateOptions,
    hasMultipleFields,
    sortedLetters
  };
};
